import React, { useState, useEffect } from 'react';
import { BordioLogin } from './components/Auth/BordioLogin';
import { BordioSidebar } from './components/Layout/BordioSidebar';
import { BordioHeader } from './components/Layout/BordioHeader';
import { BordioDashboard } from './components/Dashboard/BordioDashboard';
import { IssueBoard } from './components/Issues/IssueBoard';
import { BordioCalendar } from './components/Calendar/BordioCalendar';
import { BordioTaskList } from './components/Tasks/BordioTaskList';
import { CreateIssueModal } from './components/Modals/CreateIssueModal';
import { TeamAnalyticsDashboard } from './components/Dashboard/TeamAnalyticsDashboard';
import { ManagerDashboard } from './components/Dashboard/ManagerDashboard';
import { SprintBoard } from './components/Sprint/SprintBoard';
import { ProjectKanban } from './components/Projects/ProjectKanban';
import { JiraProjectGrid } from './components/Projects/JiraProjectGrid';
import { IssueModal } from './components/Modals/IssueModal';
import { ProfileModal } from './components/Modals/ProfileModal';
import { SettingsModal } from './components/Modals/SettingsModal';
import { SprintModal } from './components/Sprint/SprintModal';
import { ProjectModal } from './components/Projects/ProjectModal';
import { AiApiPage } from './components/AI/AiApiPage';
import { DeploymentsPanel } from './components/Deployments/DeploymentsPanel';
import { SheetsPanel } from './components/Sheets/SheetsPanel';
import { useAuth } from './hooks/useAuth';
import { useJira } from './hooks/useJira';
import { ViewMode, Issue, Project, Sprint } from './types';
import { ToastContainer } from './components/UI/Toast';
import { Sparkles, Loader2, WifiOff, RefreshCw } from 'lucide-react';

function App() {
  const {
    isAuthenticated,
    user: authUser,
    isLoading: authLoading,
    error: authError,
    permissions,
    login,
    register,
    socialLogin,
    logout,
    clearError,
    sendOtp,
    otpLogin,
  } = useAuth();

  // ─── URL-based routing helpers ──────────────────────────────────────────
  const viewRouteMap: Record<ViewMode, string> = {
    'dashboard': '/dashboard',
    'boards': '/boards',
    'calendar': '/calendar',
    'issues': '/issues',
    'sprints': '/sprints',
    'projects': '/projects',
    'team-analytics': '/team-analytics',
    'manager-dashboard': '/manager',
    'reports': '/reports',
    'filters': '/filters',
    'project-management': '/project-management',
    'ai-api': '/ai-api',
    'deployments': '/deployments',
    'sheets': '/sheets',
  };

  const routeViewMap: Record<string, ViewMode> = Object.fromEntries(
    Object.entries(viewRouteMap).map(([view, route]) => [route, view as ViewMode])
  ) as Record<string, ViewMode>;

  const validProjectTabs = ['board', 'dashboard', 'members', 'tasks', 'calendar', 'invitations', 'notes', 'settings'];

  const getViewFromPath = (): { view: ViewMode; projectId: string | null; issueKey: string | null; projectTab: string | null } => {
    const path = window.location.pathname;
    // Check for /issues/:key pattern (e.g., /issues/PROJ-123)
    const issueMatch = path.match(/^\/issues\/([A-Za-z0-9_-]+)/);
    if (issueMatch) {
      return { view: 'issues' as ViewMode, projectId: null, issueKey: issueMatch[1], projectTab: null };
    }
    // Check for /projects/:id/:tab pattern (e.g., /projects/abc123/members)
    const projectTabMatch = path.match(/^\/projects\/([^/]+)\/([^/]+)/);
    if (projectTabMatch) {
      const tab = validProjectTabs.includes(projectTabMatch[2]) ? projectTabMatch[2] : 'board';
      return { view: 'projects' as ViewMode, projectId: projectTabMatch[1], issueKey: null, projectTab: tab };
    }
    // Check for /projects/:id pattern
    const projectMatch = path.match(/^\/projects\/([^/]+)/);
    if (projectMatch) {
      return { view: 'projects' as ViewMode, projectId: projectMatch[1], issueKey: null, projectTab: 'board' };
    }
    // Check exact route matches
    for (const [route, view] of Object.entries(routeViewMap)) {
      if (path === route || path.startsWith(route + '/')) {
        return { view, projectId: null, issueKey: null, projectTab: null };
      }
    }
    return { view: 'dashboard' as ViewMode, projectId: null, issueKey: null, projectTab: null };
  };

  const initialRoute = getViewFromPath();
  const [pendingIssueKey, setPendingIssueKey] = useState<string | null>(initialRoute.issueKey);
  const [projectTab, setProjectTab] = useState<string>(initialRoute.projectTab || 'board');

  const [currentView, setCurrentViewState] = useState<ViewMode>(initialRoute.view);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isSprintModalOpen, setIsSprintModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | undefined>();
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [selectedSprint, setSelectedSprint] = useState<Sprint | undefined>();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(initialRoute.projectId);
  const [createIssueProjectId, setCreateIssueProjectId] = useState<string | undefined>();
  const [showProjectsList, setShowProjectsList] = useState(!initialRoute.projectId);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Sync URL when view changes
  const setCurrentView = (view: ViewMode) => {
    setCurrentViewState(view);
    const route = viewRouteMap[view] || '/dashboard';
    if (window.location.pathname !== route) {
      window.history.pushState({ view }, '', route);
    }
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const { view, projectId, issueKey, projectTab: tab } = getViewFromPath();
      setCurrentViewState(view);
      if (view === 'projects' && projectId) {
        setSelectedProjectId(projectId);
        setShowProjectsList(false);
        setProjectTab(tab || 'board');
      } else if (view === 'projects') {
        setSelectedProjectId(null);
        setShowProjectsList(true);
      }
      // Handle issue URL on back/forward
      if (issueKey) {
        setPendingIssueKey(issueKey);
      } else {
        setIsIssueModalOpen(false);
        setSelectedIssue(undefined);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Set initial URL on first load if at root
  useEffect(() => {
    if (window.location.pathname === '/' || window.location.pathname === '') {
      window.history.replaceState({ view: 'dashboard' }, '', '/dashboard');
    }
  }, []);

  const {
    projects,
    issues,
    users: jiraUsers,
    sprints,
    currentUser,
    isLoading: jiraLoading,
    error: jiraError,
    addSprint,
    updateSprint,
    deleteSprint,
    startSprint,
    completeSprint,
    addIssueToSprint,
    removeIssueFromSprint,
    getSprintStats,
    addProject,
    updateProject,
    deleteProject,
    addIssue,
    updateIssue,
    deleteIssue,
    getIssuesByProject,
    refreshProjectIssues,
    getDashboardStats,
    getDashboardIssueBreakdown,
    getCompletionTrends,
    searchIssues,
  } = useJira(isAuthenticated);

  // Open issue from URL when issues are loaded (e.g., /issues/PROJ-123)
  useEffect(() => {
    if (pendingIssueKey && issues.length > 0) {
      const issue = issues.find(i => i.key === pendingIssueKey || i.id === pendingIssueKey);
      if (issue) {
        setSelectedIssue(issue);
        setIsIssueModalOpen(true);
      }
      setPendingIssueKey(null);
    }
  }, [pendingIssueKey, issues]);

  // Loading screen
  const isInitialAuthCheck = authLoading && !isAuthenticated && !!localStorage.getItem('planner_token');
  if (isInitialAuthCheck || jiraLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-50 via-white to-brand-50 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <img src="/planner-logo.png" alt="Planner" className="w-11 h-11 object-contain" />
          </div>
          <div className="flex items-center gap-3 justify-center mb-3">
            <Loader2 className="w-5 h-5 text-brand-600 animate-spin" />
            <p className="text-surface-600 font-medium">
              {authLoading ? 'Checking authentication...' : 'Loading your workspace...'}
            </p>
          </div>
          <p className="text-sm text-surface-400">This won't take long</p>
        </div>
      </div>
    );
  }

  // Error screen
  if (jiraError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-50 via-white to-brand-50 flex items-center justify-center">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <WifiOff className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-surface-900 mb-2">Connection Error</h2>
          <p className="text-surface-500 mb-6">{jiraError}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            <RefreshCw className="w-4 h-4" />
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <BordioLogin
        onLogin={async (email, password) => {
          const success = await login(email, password);
          if (success) {
            setCurrentView('dashboard');
          }
          return success;
        }}
        onRegister={async (displayName, email, password) => {
          const success = await register(displayName, email, password);
          if (success) {
            setCurrentView('dashboard');
          }
          return success;
        }}
        onSocialLogin={socialLogin}
        onSendOtp={sendOtp}
        onOtpLogin={async (email, otp) => {
          const success = await otpLogin(email, otp);
          if (success) {
            setCurrentView('dashboard');
          }
          return success;
        }}
        isLoading={authLoading}
        error={authError}
      />
    );
  }

  // Handlers
  const handleIssueClick = (issue: Issue) => {
    setSelectedIssue(issue);
    setIsIssueModalOpen(true);
    // Push shareable issue URL
    if (issue.key) {
      window.history.pushState({ view: currentView, issueKey: issue.key }, '', `/issues/${issue.key}`);
    }
  };

  const handleCreateIssue = (projectId?: string) => {
    setCreateIssueProjectId(projectId);
    setIsCreateTaskModalOpen(true);
  };

  const handleIssueSave = (issueData: any) => {
    if (selectedIssue && selectedIssue.id) {
      updateIssue(selectedIssue.id, issueData);
    }
  };

  const handleCreateIssueSave = (result: any) => {
    if (result?.project_id) {
      refreshProjectIssues(result.project_id);
    } else if (result?.projectId) {
      refreshProjectIssues(result.projectId);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProjectId(project.id);
    setShowProjectsList(false);
    setCurrentViewState('projects');
    setProjectTab('board');
    window.history.pushState({ view: 'projects', projectId: project.id, tab: 'board' }, '', `/projects/${project.id}/board`);
  };

  const handleProjectEdit = (project: Project) => {
    setSelectedProject(project);
    setIsProjectModalOpen(true);
  };

  const handleProjectSave = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (selectedProject) {
      const updated = await updateProject(selectedProject.id, projectData);
      setSelectedProject(undefined);
      return updated;
    } else {
      const newProj = await addProject(projectData);
      setSelectedProject(undefined);
      return newProj;
    }
  };

  const handleSprintEdit = (sprint: Sprint) => {
    setSelectedSprint(sprint);
    setIsSprintModalOpen(true);
  };

  const handleSprintSave = (sprintData: Omit<Sprint, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (selectedSprint) {
      updateSprint(selectedSprint.id, sprintData);
    } else {
      addSprint(sprintData);
    }
    setSelectedSprint(undefined);
  };

  const handleProjectDelete = (projectId: string) => {
    deleteProject(projectId);
    if (selectedProjectId === projectId) {
      setSelectedProjectId(null);
      setShowProjectsList(true);
      window.history.pushState({ view: 'projects' }, '', '/projects');
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const getFilteredIssues = () => {
    let filteredIssues = issues;
    if (selectedProjectId) {
      filteredIssues = filteredIssues.filter(issue => issue.projectId === selectedProjectId);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filteredIssues = filteredIssues.filter(issue =>
        issue.summary.toLowerCase().includes(q) ||
        issue.key.toLowerCase().includes(q) ||
        issue.description?.toLowerCase().includes(q)
      );
    }
    return filteredIssues;
  };

  const renderContent = () => {
    const filteredIssues = getFilteredIssues();
    const currentProject = selectedProjectId ? projects.find(p => p.id === selectedProjectId) : null;

    switch (currentView) {
      case 'dashboard':
        return (
          <BordioDashboard
            stats={getDashboardStats}
            loadIssueBreakdown={getDashboardIssueBreakdown}
            loadCompletionTrends={getCompletionTrends}
            currentUser={authUser || currentUser}
            issues={issues}
            projects={projects}
            onIssueClick={handleIssueClick}
            onCreateIssue={() => handleCreateIssue()}
          />
        );

      case 'boards':
        return (
          <IssueBoard
            issues={filteredIssues}
            users={jiraUsers}
            projects={projects}
            onIssueClick={handleIssueClick}
          />
        );

      case 'calendar':
        return (
          <BordioCalendar
            issues={issues}
            users={jiraUsers}
            projects={projects}
            onCreateIssue={() => handleCreateIssue()}
            onIssueClick={handleIssueClick}
          />
        );

      case 'issues':
        return (
          <BordioTaskList
            issues={filteredIssues}
            users={jiraUsers}
            projects={projects}
            onIssueClick={handleIssueClick}
            onCreateIssue={() => handleCreateIssue()}
          />
        );

      case 'sprints':
        return (
          <SprintBoard
            sprints={sprints}
            issues={issues}
            users={jiraUsers}
            projects={projects}
            currentUser={(authUser || currentUser)!}
            onCreateSprint={() => setIsSprintModalOpen(true)}
            onEditSprint={handleSprintEdit}
            onStartSprint={startSprint}
            onCompleteSprint={completeSprint}
            onAddIssueToSprint={addIssueToSprint}
            onRemoveIssueFromSprint={removeIssueFromSprint}
            onIssueClick={handleIssueClick}
          />
        );

      case 'projects':
        if (currentProject && !showProjectsList) {
          return (
            <ProjectKanban
              project={currentProject}
              issues={issues.filter(issue => issue.projectId === currentProject.id)}
              users={jiraUsers}
              currentUser={(authUser || currentUser)!}
              onIssueClick={handleIssueClick}
              onCreateIssue={handleCreateIssue}
              onEditProject={handleProjectEdit}
              onDeleteProject={handleProjectDelete}
              onUpdateIssue={updateIssue}
              onDeleteIssue={deleteIssue}
              onBackToProjects={() => {
                setShowProjectsList(true);
                setSelectedProjectId(null);
                setProjectTab('board');
                window.history.pushState({ view: 'projects' }, '', '/projects');
              }}
              onRefreshProjectIssues={refreshProjectIssues}
              initialTab={projectTab}
              onTabChange={(tab) => {
                setProjectTab(tab);
                window.history.pushState(
                  { view: 'projects', projectId: currentProject.id, tab },
                  '',
                  `/projects/${currentProject.id}/${tab}`
                );
              }}
            />
          );
        }
        return (
          <JiraProjectGrid
            projects={projects}
            users={jiraUsers}
            onProjectClick={handleProjectSelect}
          />
        );

      case 'team-analytics':
        return (
          <TeamAnalyticsDashboard
            users={jiraUsers}
            issues={issues}
            projects={projects}
            currentUser={authUser || currentUser}
          />
        );

      case 'manager-dashboard':
        return (
          <ManagerDashboard
            issues={issues}
            users={jiraUsers}
            projects={projects}
            currentUser={authUser || currentUser}
            onIssueClick={handleIssueClick}
            onProjectSelect={handleProjectSelect}
          />
        );

      case 'sheets':
        return (
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4">
              <div className="mb-4">
                <h1 className="text-lg font-semibold text-gray-900">Sheets</h1>
                <p className="mt-0.5 text-xs text-gray-500">
                  Spreadsheet-style tables your team defines, with comments on any row.
                </p>
              </div>
              <SheetsPanel />
            </div>
          </div>
        );

      case 'deployments':
        return (
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4">
              <div className="mb-4">
                <h1 className="text-lg font-semibold text-gray-900">Deployments</h1>
                <p className="mt-0.5 text-xs text-gray-500">
                  Every release across all projects. Pipelines report themselves; anything manual can be logged by hand.
                </p>
              </div>
              <DeploymentsPanel />
            </div>
          </div>
        );

      case 'reports':
        return (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-surface-300" />
              </div>
              <h3 className="text-lg font-semibold text-surface-700 mb-2">Reports</h3>
              <p className="text-sm text-surface-500">Advanced reporting coming soon</p>
            </div>
          </div>
        );

      case 'ai-api':
        return (
          <AiApiPage
            projects={projects}
            currentUser={authUser || currentUser}
          />
        );

      default:
        return (
          <BordioDashboard
            stats={getDashboardStats}
            loadIssueBreakdown={getDashboardIssueBreakdown}
            loadCompletionTrends={getCompletionTrends}
            currentUser={authUser || currentUser}
            issues={issues}
            projects={projects}
            onIssueClick={handleIssueClick}
            onCreateIssue={() => handleCreateIssue()}
          />
        );
    }
  };

  return (
    <div className="flex h-screen bg-surface-50 overflow-hidden">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden animate-fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:block`}>
        <BordioSidebar
          currentView={currentView}
          onViewChange={(view) => {
            setCurrentView(view);
            setIsMobileMenuOpen(false);
            if (view !== 'projects') {
              setShowProjectsList(true);
            }
          }}
          projects={projects}
          onNewProject={() => setIsProjectModalOpen(true)}
          onProjectSelect={(projectId: string) => {
            const project = projects.find(p => p.id === projectId);
            if (project) handleProjectSelect(project);
            setIsMobileMenuOpen(false);
          }}
          currentUser={authUser || currentUser}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <BordioHeader
          onSearchChange={handleSearchChange}
          currentUser={authUser || currentUser}
          currentView={currentView}
          onProfileClick={() => setIsProfileModalOpen(true)}
          onSettingsClick={() => setIsSettingsModalOpen(true)}
          onMenuClick={() => setIsMobileMenuOpen(true)}
          onLogout={logout}
          onCreateIssue={() => handleCreateIssue()}
          onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
        />

        <main className="flex-1 overflow-auto">
          {renderContent()}
        </main>
      </div>

      {/* Toast Container */}
      <ToastContainer />

      {/* Modals */}
      <CreateIssueModal
        isOpen={isCreateTaskModalOpen}
        onClose={() => {
          setIsCreateTaskModalOpen(false);
          setCreateIssueProjectId(undefined);
        }}
        onSave={handleCreateIssueSave}
        users={jiraUsers}
        projects={projects}
        preSelectedProjectId={createIssueProjectId}
        currentUser={(authUser || currentUser) ?? undefined}
      />

      <IssueModal
        isOpen={isIssueModalOpen}
        onClose={() => {
          setIsIssueModalOpen(false);
          setSelectedIssue(undefined);
          // Restore the current view URL when closing issue modal
          const route = viewRouteMap[currentView] || '/dashboard';
          if (currentView === 'projects' && selectedProjectId) {
            window.history.pushState({ view: 'projects', projectId: selectedProjectId, tab: projectTab }, '', `/projects/${selectedProjectId}/${projectTab}`);
          } else {
            window.history.pushState({ view: currentView }, '', route);
          }
        }}
        onSave={handleIssueSave}
        users={jiraUsers}
        projects={projects}
        issue={selectedIssue}
        currentUser={(authUser || currentUser) ?? undefined}
      />

      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => {
          setIsProjectModalOpen(false);
          setSelectedProject(undefined);
        }}
        onSave={handleProjectSave}
        users={jiraUsers}
        project={selectedProject}
      />

      <SprintModal
        isOpen={isSprintModalOpen}
        onClose={() => {
          setIsSprintModalOpen(false);
          setSelectedSprint(undefined);
        }}
        onSave={handleSprintSave}
        users={jiraUsers}
        projects={projects}
        sprint={selectedSprint}
        currentUser={(authUser || currentUser)!}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={(authUser || currentUser)!}
        onUpdateProfile={(updates) => console.log('Profile updates:', updates)}
      />

      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </div>
  );
}

export default App;
